/**
 * Native keyboard → layout height.
 *
 * With `KeyboardResize.None` (chosen to avoid the janky native frame resize and
 * the black gap behind the keyboard's rounded corners) iOS does NOT shrink the
 * visual viewport when the keyboard opens, so the CSS `--app-height` driven by
 * `visualViewport` (see viewport-height.ts) stays at full height and the
 * composer ends up hidden behind the keyboard.
 *
 * Here we drive `--app-height` directly from the Capacitor Keyboard events,
 * which fire with the keyboard height at the start of the show/hide animation.
 * The chat layout is sized to `--app-height`, so it shrinks to sit just above
 * the keyboard. No-op on the web (the visualViewport path covers mobile Safari).
 */
import { isNativeShell } from './platform';

export async function initNativeKeyboard(): Promise<void> {
  if (!isNativeShell()) return;

  const root = document.documentElement;
  const setHeight = (px: number) => root.style.setProperty('--app-height', `${Math.round(px)}px`);
  const resetHeight = () => setHeight(window.innerHeight);

  try {
    const { Keyboard } = await import('@capacitor/keyboard');

    await Keyboard.addListener('keyboardWillShow', (info) => {
      // Full webview height minus the keyboard leaves the visible area.
      setHeight(window.innerHeight - info.keyboardHeight);
      // The keyboard covers the home-indicator area, so bottom-docked UI
      // (composer footer) must drop its safe-area inset while the keyboard is
      // up — otherwise it floats ~34px above the keyboard. Consumers read this
      // via `var(--keyboard-inset-bottom, env(safe-area-inset-bottom))`.
      root.style.setProperty('--keyboard-inset-bottom', '0px');
    });
    await Keyboard.addListener('keyboardWillHide', () => {
      resetHeight();
      root.style.removeProperty('--keyboard-inset-bottom');
    });

    resetHeight();
  } catch {
    // Plugin unavailable — leave the visualViewport-driven height in place.
  }
}
